import React, { useState } from 'react';
import { useSubscription } from '../../context/SubscriptionContext';
import { FeatureKey } from '../../types/subscription';
import UpgradePrompt from './UpgradePrompt';

interface FeatureGateProps {
  feature: FeatureKey;
  children: React.ReactNode;
  showUpgradePrompt?: boolean;
  fallback?: React.ReactNode;
}

const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  children,
  showUpgradePrompt = true,
  fallback,
}) => {
  const { canAccess, loading } = useSubscription();
  const [showModal, setShowModal] = useState(false);

  // While loading, show a placeholder or the children (optimistic)
  if (loading) {
    return <>{children}</>;
  }

  // If user has access, render children
  if (canAccess(feature)) {
    return <>{children}</>;
  }

  // User doesn't have access
  if (showUpgradePrompt) {
    return (
      <>
        <div
          onClick={() => setShowModal(true)}
          className="cursor-pointer"
        >
          {fallback || (
            <div className="relative">
              <div className="opacity-50 pointer-events-none">
                {children}
              </div>
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900/10 rounded-lg">
                <div className="bg-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-yellow-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">
                    Upgrade to unlock
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
        <UpgradePrompt
          feature={feature}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
        />
      </>
    );
  }

  // No upgrade prompt, just show fallback or nothing
  return <>{fallback || null}</>;
};

export default FeatureGate;
